import {
  AutocompleteProps,
  Box,
  InputLabel,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { forwardRef } from "react";
import { AutocompleteMenuProps } from "src/@types/components/autocomplete";
import ChevronDown from "src/assets/icon/chevron/ChevronDown";
import { AutoComplete } from "src/components/FormFields";
import useLocales from "src/hooks/useLocales";
import MyErrorHelperText from "../denseForm/ErrorHelperText";
import MyHelperText from "../denseForm/HelperText";
import MyMenuItem from "../denseForm/MenuItem";
import { BootstrapInput } from "./TextField";

type MyAutoCompleteProps = {
  label?: string;
  options: any[];
  name: string;
  displayKey: string;
  onChange: (e: any, value: any) => void;
  val: any;
  error: string | undefined;
  placeholder?: string;
  optionTooltipProps?: any;
} & AutocompleteMenuProps;

export function ReviewAutoComplete({
  label,
  options,
  name,
  displayKey,
  onChange,
  val,
  placeholder,
  error,
  optionTooltipProps,
  menuOptionProps = {
    showValueKey: false,
  },
}: MyAutoCompleteProps) {
  return (
    <AutoComplete
      displayKey={displayKey}
      name={name}
      val={val}
      options={options}
      onChange={onChange}
      error={!!error}
      errorText={error}
      label={label}
      optionTooltipProps={optionTooltipProps}
      placeholder={placeholder}
      menuOptionProps={menuOptionProps}
      style={{ fontSize: "12px" }}
      sx={{
        width: { xs: 160, sm: 160, md: 160, lg: 200, xl: 240 },
        height: 35,
        align: "center",
      }}
    />
  );
}

type NewAutoCompleteFieldProps = {
  label?: string;
  options: any[];
  onChange: (e: any, value: any) => void;
  error?: string | undefined;
  placeholder?: string;
  value: any;
  disabled?: boolean;
  renderInput?: any;
  optionTooltipProps?: any;
  helperText?: string;
};
export const NewAutoCompleteField = forwardRef(
  (
    {
      label,
      options,
      onChange,
      error,
      placeholder,
      value,
      disabled,
      renderInput,
      optionTooltipProps,
      helperText,
      ...props
    }: NewAutoCompleteFieldProps &
      Omit<
        AutocompleteProps<any, boolean, boolean, boolean, any>,
        "renderInput"
      >,
    ref
  ) => {
    const { translate } = useLocales();
    const theme = useTheme();
    return (
      <Box>
        <MuiAutocomplete
          size="small"
          value={value}
          disabled={disabled}
          onChange={onChange}
          selectOnFocus
          clearOnBlur
          options={options}
          onKeyDown={(event) => event.stopPropagation()}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return translate(option);
            }
            return translate(option.label);
          }}
          isOptionEqualToValue={(option, value) => {
            return option.enum === value || option.value === value;
          }}
          renderOption={(props: any, option: any, state: object) => (
            <MyMenuItem
              {...{
                option,
                displayKey: "label",
                ...props,
                optionTooltipProps,
                // valueDisplayKey: "enum",
                // showValueKey: true,
              }}
            />
          )}
          popupIcon={
            <ChevronDown
              height={24}
              width={22}
              sx={{
                p: "3px",
                mr: `2px`,
              }}
            />
          }
          renderInput={({ ...params }) => {
            const { InputLabelProps, InputProps, ...restParams } = params;
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  alignItems: "flex-start",
                  p: 0,
                  gap: "4px",
                }}
              >
                {label && (
                  <InputLabel
                    htmlFor="demo-customized-textbox"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "action.active",
                    }}
                  >
                    {label}
                  </InputLabel>
                )}
                <BootstrapInput
                  endAdornment={params.InputProps?.endAdornment}
                  ref={InputProps?.ref}
                  placeholder={placeholder}
                  sx={{
                    "& .MuiInputBase-input": {
                      ...(!!error && {
                        backgroundColor: theme.palette.error.light,
                        borderColor: theme.palette.error.dark,
                        color: theme.palette.error.dark,
                        "&::placeholder": {
                          opacity: 1,
                          color: theme.palette.error.dark,
                        },
                        "&:focus": {
                          borderColor: theme.palette.error.dark,
                          boxShadow: `0 0 0 0.01rem ${theme.palette.error.dark}`,
                        },
                      }),
                    },
                  }}
                  {...restParams}
                />
                {error ? (
                  <MyErrorHelperText error={!!error} helperText={error} />
                ) : helperText ? (
                  <MyHelperText helperText={helperText} />
                ) : null}
              </Box>
            );
          }}
          {...props}
        />
      </Box>
    );
  }
);
